import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button, AlertMessage, Spinner, FileUploadField, SelectField } from "@/components/reusable";
import { FilePreviewDialog } from "@/components/upload/FilePreviewDialog";
import { classifyFile, fetchTargetSchemas, previewFile, uploadFile } from "@/services/api";
import { useUpload } from "@/hooks/useUpload";
import { useTelemetry } from "@/hooks/useTelemetry";
import { useNavigateKeepParams } from "@/hooks/useNavigateKeepParams";
import type { TargetSchema, ParsedWorksheet } from "@/types";
// TODO: Hardcoded for now, will be migrated to config file. Allows report type selection for specific schemas.
const REQUIRED_REPORT_TYPE_SCHEMAS = ["Lloyds Coverholder Reporting Standard"];

export function UploadPage() {
  const { t } = useTranslation();
  const navigate = useNavigateKeepParams();
  const {
    confirmUpload,
    resetUpload,
    setClassifying,
    setClassificationResult,
    setReportType,
    reportType,
    detectedType,
    allowedReportTypes,
    isClassifying,
  } = useUpload();

  useEffect(() => {
    resetUpload();
  }, [resetUpload]);
  const { track, trackError, startTimer } = useTelemetry();

  // Form state
  const [file, setFile] = useState<File | null>(null);
  const [schemas, setSchemas] = useState<TargetSchema[]>([]);
  const [selectedSchema, setSelectedSchema] = useState("");
  const [error, setError] = useState("");
  const [isLoadingSchemas, setIsLoadingSchemas] = useState(true);
  const [schemaSelectTouched, setSchemaSelectTouched] = useState(false);

  // Preview dialog
  const [showPreview, setShowPreview] = useState(false);
  const [worksheets, setWorksheets] = useState<ParsedWorksheet[]>([]);
  const [isParsing, setIsParsing] = useState(false);

  // Upload state
  const [isUploading, setIsUploading] = useState(false);

  // Load schemas on mount
  useEffect(() => {
    const stopTimer = startTimer();
    setIsLoadingSchemas(true);

    fetchTargetSchemas()
      .then((data) => {
        setSchemas(data);
        if (data.length === 0) {
          setError("No target schemas found. The schema table may be empty or misconfigured.");
        }
        track("schemas_loaded", {
          schemaCount: data.length,
          durationMs: stopTimer(),
        });
      })
      .catch((err) => {
        setError(String(err));
        trackError(err, { context: "schema_load" });
      })
      .finally(() => setIsLoadingSchemas(false));
  }, [startTimer, track, trackError]);

  const isFormValid = file !== null && selectedSchema !== "";

  // Track file selection
  const handleFileSelect = useCallback(
    (selectedFile: File | null) => {
      setSchemaSelectTouched(false);
      setFile(selectedFile);
      if (selectedFile) {
        track("file_selected", {
          fileExtension: selectedFile.name.split(".").pop()?.toLowerCase() ?? "unknown",
          fileSizeKB: Math.round(selectedFile.size / 1024),
          fileType: selectedFile.type || "unknown",
        });
      }
    },
    [track]
  );

  // Track schema selection
  const handleSchemaChange = useCallback(
    (schemaId: string) => {
      if (isLoadingSchemas) return;
      setSelectedSchema(schemaId);
      const schemaName = schemas.find((s) => s.id === schemaId)?.name ?? schemaId;
      track("schema_selected", { schemaId, schemaName });
      // Clear any prior classification state when switching schemas so
      // non-classified schemas don't inherit stale report_type state.
      setReportType(null);
      setClassificationResult({ detectedType: null, allowedReportTypes: [] });
      setClassifying(false);
    },
    [isLoadingSchemas, schemas, track, setReportType, setClassificationResult, setClassifying]
  );

  const handleBeginConversion = useCallback(async () => {
    if (!file) return;
    setIsParsing(true);
    setError("");

    track("conversion_started", {
      fileExtension: file.name.split(".").pop()?.toLowerCase() ?? "unknown",
      schemaId: selectedSchema,
    });

    const stopTimer = startTimer();

    // Fire preview and (optionally) classification in parallel. The
    // preview API drives the dialog contents. Classification is only
    // required for schemas that need a report_type before proceeding
    // (e.g. Lloyds Coverholder Reporting Standard).
    const schemaName = schemas.find((s) => s.id === selectedSchema)?.name ?? selectedSchema;
    // TODO: Hardcoded for now, will be migrated to config file.
    const requiresReportType = REQUIRED_REPORT_TYPE_SCHEMAS.includes(schemaName);
    const classifyPromise = requiresReportType
      ? (() => {
          setClassifying(true);
          return classifyFile(file, selectedSchema)
            .then((result) => {
              setClassificationResult({
                detectedType: result.detectedType,
                allowedReportTypes: result.allowedReportTypes,
              });
              track("Classify.Succeeded", {
                schemaId: selectedSchema,
                detectedType: result.detectedType,
                stepUsed: result.stepUsed,
                fileName: file.name,
              });
            })
            .catch((err) => {
              // Classification failure is non-blocking — set an empty result.
              // The validation on "Confirm" will force the user to pick a
              // value manually for schemas that require it.
              setClassificationResult({
                detectedType: null,
                allowedReportTypes: [],
              });
              trackError(err, {
                context: "file_classify",
                schemaId: selectedSchema,
              });
            });
        })()
      : Promise.resolve();

    try {
      const parsed = await previewFile(file);
      const durationMs = stopTimer();
      setWorksheets(parsed);
      setShowPreview(true);

      const totalRows = parsed.reduce((sum: number, ws) => sum + ws.rows.length, 0);
      track(
        "file_parsed",
        {
          fileExtension: file.name.split(".").pop()?.toLowerCase() ?? "unknown",
          worksheetCount: parsed.length,
          schemaId: selectedSchema,
        },
        { durationMs, totalRows }
      );
    } catch (err) {
      const durationMs = stopTimer();
      setError(`Failed to parse file: ${err instanceof Error ? err.message : String(err)}`);
      trackError(err, {
        context: "file_parse",
        fileExtension: file.name.split(".").pop()?.toLowerCase() ?? "unknown",
        durationMs,
      });
    } finally {
      setIsParsing(false);
      // Ensure the spinner drops even if the user closes the dialog
      // before classification completes — we still await to catch errors
      // but the awaited work above owns the UI state transitions.
      void classifyPromise;
    }
  }, [
    file,
    selectedSchema,
    schemas,
    track,
    trackError,
    startTimer,
    setClassifying,
    setClassificationResult,
  ]);

  const handleConfirmUpload = useCallback(async () => {
    if (!file) return;

    const schemaName = schemas.find((s) => s.id === selectedSchema)?.name ?? selectedSchema;

    // --- Upload file to sidecar API ---
    setIsUploading(true);
    setError("");

    track("FileUpload.Started", {
      fileName: file.name,
      fileSize: file.size,
      schemaId: selectedSchema,
    });

    const stopTimer = startTimer();

    try {
      const confirmation = await uploadFile(file, selectedSchema);
      const durationMs = stopTimer();

      if (confirmation.status !== "success") {
        throw new Error(confirmation.message || "Upload returned error status");
      }

      track("FileUpload.Succeeded", {
        documentId: confirmation.documentId,
        fileName: confirmation.fileName,
        fileSize: confirmation.fileSize ?? file.size,
        storagePath: confirmation.storagePath ?? "",
        durationMs,
      });

      // Store parsed data + file reference + documentId in context.
      // reportType is the user-confirmed File Type from the Preview
      // dropdown — persisted so MappingPage can forward it to /process.
      confirmUpload({
        file,
        fileName: file.name,
        documentId: confirmation.documentId,
        schemaId: selectedSchema,
        schemaName,
        worksheets,
        reportType,
      });

      track("upload_confirmed", {
        documentId: confirmation.documentId,
        fileExtension: file.name.split(".").pop()?.toLowerCase() ?? "unknown",
        schemaId: selectedSchema,
        schemaName,
        worksheetCount: worksheets.length,
        reportType: reportType ?? "unset",
      });

      setShowPreview(false);

      // Navigate to mapping
      navigate("/mapping");
    } catch (err) {
      const durationMs = stopTimer();
      const message = err instanceof Error ? err.message : String(err);
      setError(`Upload failed: ${message}`);
      setShowPreview(false);

      track("FileUpload.Failed", {
        fileName: file.name,
        fileSize: file.size,
        errorMessage: message,
        durationMs,
      });
      trackError(err, { context: "file_upload" });
    } finally {
      setIsUploading(false);
    }
  }, [
    file,
    selectedSchema,
    schemas,
    worksheets,
    confirmUpload,
    navigate,
    track,
    trackError,
    startTimer,
    reportType,
  ]);

  const handlePreviewCancel = useCallback(() => {
    track("upload_preview_cancelled", { schemaId: selectedSchema });
    setShowPreview(false);
  }, [selectedSchema, track]);

  const handleUploadError = useCallback(
    (err: string) => {
      setError(err);
      track("upload_error_displayed", { message: err });
    },
    [track]
  );

  const schemaOptions = schemas.map((s) => ({ value: s.id, label: s.name }));
  const selectedSchemaName = schemas.find((s) => s.id === selectedSchema)?.name ?? selectedSchema;
  // TODO: Hardcoded for now, will be migrated to config file.
  const requiresReportType = REQUIRED_REPORT_TYPE_SCHEMAS.includes(selectedSchemaName);

  return (
    <div className="flex items-start gap-12 min-h-[calc(100vh-12rem)]">
      {/* Left: Form */}
      <div className="flex-1 max-w-xl pt-8">
        <h1
          className="text-4xl md:text-5xl leading-tight mb-10 text-[var(--color-text-primary)]"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {t("upload.title")}
        </h1>

        <div className="space-y-6">
          {/* File upload */}
          <div className="animate-fade-in-up stagger-1">
            <label className="block text-sm font-semibold text-[var(--color-text-primary)] mb-2">
              {t("upload.fileLabel")}
            </label>
            <FileUploadField
              file={file}
              onFileSelect={handleFileSelect}
              onError={handleUploadError}
            />
          </div>

          {/* Schema selection */}
          <div
            className="relative"
            onMouseDown={() => setSchemaSelectTouched(true)}
            onFocus={() => setSchemaSelectTouched(true)}
          >
            <SelectField
              value={selectedSchema}
              onValueChange={handleSchemaChange}
              options={schemaOptions}
              placeholder={
                schemaSelectTouched && isLoadingSchemas
                  ? "Loading schemas..."
                  : t("upload.schemaPlaceholder")
              }
              disabled={!file}
            />

            {schemaSelectTouched && isLoadingSchemas && (
              <div className="absolute inset-y-0 right-10 flex items-center pointer-events-none">
                <Spinner size="sm" />
              </div>
            )}
          </div>

          {/* Begin Conversion */}
          <div className="animate-fade-in-up stagger-3">
            <Button
              onClick={handleBeginConversion}
              disabled={!isFormValid || isParsing || isUploading || isLoadingSchemas}
              size="lg"
              className="mt-2"
              data-testid="begin-conversion-btn"
            >
              {isParsing
                ? t("common.loading")
                : isUploading
                  ? "Uploading..."
                  : t("upload.beginConversion")}
            </Button>
          </div>

          {/* Error message */}
          {error && (
            <AlertMessage variant="error" message={error} className="mt-4 animate-scale-in" />
          )}
        </div>
      </div>

      {/* Right: Decorative area */}
      <div className="hidden lg:block flex-1 relative">
        <div className="absolute inset-0 rounded-2xl overflow-hidden opacity-[0.08] gradient-pulse">
          <div
            className="w-full h-full"
            style={{
              background: `
                  radial-gradient(circle at 30% 40%, var(--color-brand-primary) 0%, transparent 50%),
                  radial-gradient(circle at 70% 60%, var(--color-brand-accent) 0%, transparent 50%),
                  linear-gradient(135deg, var(--color-bg-secondary) 0%, var(--color-bg-tertiary) 100%)
                `,
            }}
          />
        </div>
      </div>

      {/* Preview Dialog */}
      <FilePreviewDialog
        open={showPreview}
        onOpenChange={setShowPreview}
        fileName={file?.name ?? ""}
        schemaName={selectedSchemaName}
        worksheets={worksheets}
        onConfirm={handleConfirmUpload}
        onCancel={handlePreviewCancel}
        isUploading={isUploading}
        // ── Intelligent File Classification props ──
        // These come from useUpload context and are updated by the
        // parallel classifyFile call above. The dialog renders the File
        // Type dropdown and gates its Confirm button on reportType.
        requiresReportType={requiresReportType}
        reportType={reportType}
        detectedType={detectedType}
        allowedReportTypes={allowedReportTypes}
        isClassifying={isClassifying}
        onReportTypeChange={setReportType}
      />
    </div>
  );
}
